import React, {
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useState,
  type ForwardedRef,
} from 'react';
import type { MenuItem } from '@redturtle/qubicaamf-theme/types/dropdown-menu';
import {
  Button,
  Popover,
  Dialog,
  OverlayTriggerStateContext,
} from 'react-aria-components';
// @ts-expect-error
import { type OverlayTriggerState } from 'react-stately';
import styles from './styles.module.css';
import { Container as SemanticContainer } from 'semantic-ui-react';
import {
  hasBlocksData,
  getBlocksFieldname,
  getBlocksLayoutFieldname,
  getBaseUrl,
} from '@plone/volto/helpers';
import { useLocation } from 'react-router-dom';
import { useActiveMenuContext } from './MegaMenu';
import config from '@plone/registry';
import { UniversalLink } from '@plone/volto/components';
import { useToolbarPosition } from '@redturtle/qubicaamf-theme/helpers/hooks/useToolbarPosition.hook';
import { useEventListener } from 'usehooks-ts';
import { defineMessages, useIntl } from 'react-intl';
import cx from 'classnames';
import CtaIcon from '@redturtle/qubicaamf-theme/components/Icons/CtaIcon';
import type { DropdownMenuProps } from './DropdownMenu';
import StyleWrapper from '@plone/volto/components/manage/Blocks/Block/StyleWrapper';

type MenuItemComponentProps = {
  item: MenuItem & { order?: number };
};

const messages = defineMessages({
  unknownBlock: {
    id: 'unknownBlock',
    defaultMessage: 'Unknown block type: {block}',
  },
  a11yddmTrigger: {
    id: 'a11yddmTrigger',
    defaultMessage: 'Press enter to open {title} menu',
  },
  a11yddmDialog: {
    id: 'a11yddmDialog',
    defaultMessage: '{title} menu',
  },
});

const MenuItemComponent: React.FC<MenuItemComponentProps> = ({ item }) => {
  return (
    <li className={styles['dropdown-menu-l1-item']}>
      <UniversalLink
        href={item['@id']}
        className={styles['dropdown-menu-navitem']}
        openLinkInNewTab={false}
      >
        {item.title}
      </UniversalLink>
      <ul>
        {item?.items?.map((fc, i) => (
          <li key={fc['@id']} className={styles['dropdown-menu-l2-item']}>
            <UniversalLink href={fc['@id']} openLinkInNewTab={false}>
              {fc.title}
            </UniversalLink>
          </li>
        ))}
      </ul>
    </li>
  );
};

const DropdownMenuItem = forwardRef(
  ({ item }: DropdownMenuProps, ref: ForwardedRef<HTMLButtonElement>) => {
    const state = useContext<OverlayTriggerState>(OverlayTriggerStateContext)!;
    const { left } = useToolbarPosition();
    const { activeMenuItem, setActiveMenuItem } = useActiveMenuContext();
    const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});
    const globalDropdownCloseTimeoutRef = useRef<number | null>(null);
    const updatePopoverDimensions = () => {
      const navigationElement = document.getElementById('navigation');
      if (navigationElement) {
        const navWidth = navigationElement.offsetWidth; // Impostiamo la larghezza
        setPopoverStyle({
          width: navWidth, // Larghezza dinamica
          maxWidth: 'unset', // Imposta un limite massimo per la larghezza
          margin: 'auto',
        });
      }
    };
    const location = useLocation();
    const intl = useIntl();
    const { items, showMoreIcon, showMoreLink, showMoreText } = item;
    const hasBlocks = hasBlocksData(item);
    const blocksFieldname = getBlocksFieldname(item);
    const blocksLayoutFieldname = getBlocksLayoutFieldname(item);

    const Container =
      config.getComponent('Container').component || SemanticContainer;
    useEventListener('resize', updatePopoverDimensions);

    useEffect(() => {
      if (activeMenuItem !== item.navigationRoot[0]?.['@id']) state.close();
    }, [activeMenuItem]);
    return (
      <>
        <div
          className={cx(styles['dropdown-menu-navitem'], styles['trigger'])}
          onMouseEnter={() => {
            if (globalDropdownCloseTimeoutRef.current) {
              clearTimeout(globalDropdownCloseTimeoutRef.current);
              globalDropdownCloseTimeoutRef.current = null;
            }
            setActiveMenuItem(item.navigationRoot[0]?.['@id']);
            setTimeout(() => state.open(), 50);
          }}
          onMouseLeave={() => {
            globalDropdownCloseTimeoutRef.current = setTimeout(
              () => state.close(),
              150,
            );
          }}
        >
          <UniversalLink
            href={item.navigationRoot[0]?.['@id']}
            openLinkInNewTab={false}
            className={cx({
              [styles['activeMenuLink']]: state.isOpen,
            })}
          >
            {item.title}
          </UniversalLink>
          <Button
            ref={ref}
            onPress={() => {
              state.toggle();
              setActiveMenuItem(item.navigationRoot[0]?.['@id']);
            }}
            aria-label={intl.formatMessage(messages.a11yddmTrigger, {
              title: item.title,
            })}
          >
            <svg
              viewBox="0 0 24 24"
              className={styles['submenu-sheet-nav-list-button-svg']}
              style={{
                strokeWidth: 3,
                width: '1.25rem',
                height: '1.25rem',
                marginRight: 0,
              }}
            >
              <path d="m8.25 4.5 7.5 7.5-7.5 7.5"></path>
            </svg>
          </Button>
        </div>
        <Popover isNonModal className={styles['dropdown-menu-wrapper']}>
          <div
            id="navmenu"
            onMouseLeave={() => {
              globalDropdownCloseTimeoutRef.current = setTimeout(
                () => state.close(),
                150,
              );
            }}
            onMouseEnter={() => {
              if (globalDropdownCloseTimeoutRef.current) {
                clearTimeout(globalDropdownCloseTimeoutRef.current);
                globalDropdownCloseTimeoutRef.current = null;
              }
            }}
          >
            <Dialog
              className={styles['dropdown-menu-dialog']}
              style={{ paddingLeft: left }}
              aria-label={intl.formatMessage(messages.a11yddmDialog, {
                title: item.title,
              })}
            >
              <Container
                style={popoverStyle}
                className={styles.containerWrapper}
              >
                <nav className={styles['dropdown-menu-content']}>
                  <ul className={styles['dropdown-menu-items']}>
                    {items
                      ?.sort((a, b) => a.order - b.order)
                      .map((subItem, index) => (
                        <MenuItemComponent key={index} item={subItem} />
                      ))}
                  </ul>
                  {showMoreLink?.length > 0 && showMoreText ? (
                    <div className={styles['showmore']}>
                      <UniversalLink
                        href={showMoreLink[0]?.['@id']}
                        className={styles['showmore-cta']}
                        openLinkInNewTab={false}
                      >
                        <CtaIcon iconValue={showMoreIcon} /> {showMoreText}
                      </UniversalLink>
                    </div>
                  ) : null}
                </nav>
                {hasBlocks && (
                  <div className={styles['dropdown-menu-content-blocks']}>
                    {item?.[blocksLayoutFieldname]?.items?.map((block) => {
                      const blockType =
                        item[blocksFieldname]?.[block]?.['@type'];
                      if (
                        ['title', 'pageDescription', 'empty'].indexOf(
                          blockType,
                        ) > -1
                      )
                        return null;
                      const Block =
                        config.blocks.blocksConfig[blockType]?.['view'] ?? null;
                      return Block !== null ? (
                        <StyleWrapper
                          data={item[blocksFieldname][block]}
                          block={block}
                          key={block}
                        >
                          <Block
                            id={block}
                            properties={item}
                            data={item[blocksFieldname][block]}
                            path={getBaseUrl(location?.pathname || '')}
                          />
                        </StyleWrapper>
                      ) : (
                        <div key={block}>
                          {intl.formatMessage(messages.unknownBlock, {
                            block: item[blocksFieldname]?.[block]?.['@type'],
                          })}
                        </div>
                      );
                    })}
                  </div>
                )}
              </Container>
            </Dialog>
          </div>
        </Popover>
      </>
    );
  },
);

export { DropdownMenuItem };
